import { z } from "zod";

// contains / not_contains: values are substrings, case-insensitive - the Clicks
// fields only (clicks_filter_sql, 20261103 "clicks_filter_contains").
const urlFilterModeSchema = z.enum(["include", "exclude", "contains", "not_contains"]);

const urlFilterMultiEntrySchema = z.object({
  mode: urlFilterModeSchema,
  values: z.array(z.string()),
  /** is_own_bot only: the bot rules of a preset (src/lib/bot-presets.ts). */
  rules: z.array(z.string()).optional(),
});

const urlFilterSlugEntrySchema = z.object({
  mode: urlFilterModeSchema,
  value: z.string(),
});

/** One filter row from URL JSON: multiselect (`values`) or slug (`value`). */
export const urlFilterEntrySchema = z.union([
  urlFilterMultiEntrySchema,
  urlFilterSlugEntrySchema,
]);

/** What every page's filters do: is / is not. */
export type UrlFilterMode = "include" | "exclude";
/** ...plus the text modes, which only the Clicks filters understand. */
export type UrlFilterAnyMode = z.infer<typeof urlFilterModeSchema>;
export type UrlFilterMultiEntry = z.infer<typeof urlFilterMultiEntrySchema>;
export type UrlFilterSlugEntry = z.infer<typeof urlFilterSlugEntrySchema>;
export type UrlFilterEntry = z.infer<typeof urlFilterEntrySchema>;

/** Parsed `filters` search param for RPC `p_filters` */
export type UrlActiveFilters = Record<string, UrlFilterEntry>;

export const isUrlFilterMultiEntry = (
  entry: UrlFilterEntry,
): entry is UrlFilterMultiEntry => "values" in entry;

export const isUrlFilterSlugEntry = (
  entry: UrlFilterEntry,
): entry is UrlFilterSlugEntry => "value" in entry;

export const getUrlFilterMultiValues = (
  entry: UrlFilterEntry | undefined,
): string[] => (entry && isUrlFilterMultiEntry(entry) ? entry.values : []);

/** include / exclude for the pages that know only those; a text mode reads as include there. */
export const getUrlFilterMode = (
  entry: UrlFilterEntry | undefined,
): UrlFilterMode => (entry?.mode === "exclude" ? "exclude" : "include");

export const getUrlFilterSlugValue = (
  entry: UrlFilterEntry | undefined,
): string => (entry && isUrlFilterSlugEntry(entry) ? entry.value : "");

/** Keys that use `{ mode, value }` instead of `{ mode, values }` in `p_filters`. */
export const URL_SLUG_FILTER_KEYS = new Set(["slug", "query"]);

export const isUrlSlugFilterKey = (key: string): boolean =>
  URL_SLUG_FILTER_KEYS.has(key);

export const countUrlActiveFilters = (filters: UrlActiveFilters): number =>
  Object.keys(filters).length;

export const serializeUrlActiveFilters = (
  filters: UrlActiveFilters,
): string | null =>
  Object.keys(filters).length > 0 ? JSON.stringify(filters) : null;

const jsonObjectSchema = z.record(z.string(), z.unknown());

/**
 * Safe parse of the `filters` URL param (JSON object for PostgREST RPC).
 * Invalid JSON, non-objects, or bad entries are skipped; fully bad input → `{}`.
 * A value filter with no values is dropped - it filters nothing and showed as
 * an "(empty)" pill; the bot filter keeps an empty list (its preset's rules
 * still drive the Bots columns).
 */
export const parseUrlActiveFilters = (
  filtersParam: string | null,
): UrlActiveFilters => {
  if (!filtersParam?.trim()) return {};

  // A guard against pathological input, not a product limit: 50 000 cut off
  // ~1 200 domains and silently dropped every filter (27.10.2026).
  if (filtersParam.length > 2_000_000) return {};

  let parsedJson: unknown;
  try {
    parsedJson = JSON.parse(filtersParam) as unknown;
  } catch {
    return {};
  }

  const objectResult = jsonObjectSchema.safeParse(parsedJson);
  if (!objectResult.success) return {};

  const out: UrlActiveFilters = {};
  for (const [key, val] of Object.entries(objectResult.data)) {
    const entry = urlFilterEntrySchema.safeParse(val);
    if (!entry.success) continue;
    if (key !== "is_own_bot" && isUrlFilterMultiEntry(entry.data) && entry.data.values.length === 0) continue;
    out[key] = entry.data;
  }
  return out;
};
